import React, { useState } from "react";
import { useQuery, useLazyQuery, useMutation, gql } from "@apollo/client";

// Every professional's leave balance per pool for the current period, the
// ledger behind it, and manual credits / debits (opening balances, comp-off
// granted by admin, corrections).

const GET_BALANCES = gql`
  query GetLeaveBalances {
    getLeaveBalances {
      status
      message
      pools
      rows {
        employeeId
        fullName
        balances {
          pool
          balance
        }
      }
    }
  }
`;

const GET_LEDGER = gql`
  query GetLeaveBalanceLedger($employeeId: ID!, $pool: String) {
    getLeaveBalanceLedger(employeeId: $employeeId, pool: $pool) {
      id
      balancePool
      entryType
      units
      date
      note
    }
  }
`;

const ADJUST = gql`
  mutation AdjustLeaveBalance($data: AdjustLeaveBalanceInput!) {
    adjustLeaveBalance(data: $data) {
      status
      message
    }
  }
`;

const RUN_ACCRUAL = gql`
  mutation RunLeaveAccrual {
    runLeaveAccrual {
      status
      message
    }
  }
`;

const fmt = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));

const LeaveBalances = () => {
  const { data, loading, refetch } = useQuery(GET_BALANCES, { fetchPolicy: "network-only" });
  const [loadLedger, { data: ledgerData, loading: ledgerLoading, refetch: refetchLedger }] =
    useLazyQuery(GET_LEDGER, { fetchPolicy: "network-only" });
  const [adjust] = useMutation(ADJUST);
  const [runAccrual] = useMutation(RUN_ACCRUAL);

  const [selected, setSelected] = useState(null); // { employeeId, fullName, pool }
  const [units, setUnits] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const result = data?.getLeaveBalances;
  const pools = result?.pools || [];
  const rows = (data?.getLeaveBalances?.rows || []).filter((r) =>
    (r.fullName || "").toLowerCase().includes(search.toLowerCase()),
  );
  const ledger = ledgerData?.getLeaveBalanceLedger || [];

  const openLedger = (row, pool) => {
    setSelected({ employeeId: row.employeeId, fullName: row.fullName, pool });
    setUnits("");
    setNote("");
    setError("");
    loadLedger({ variables: { employeeId: row.employeeId, pool } });
  };

  const submitAdjustment = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await adjust({
        variables: {
          data: {
            employeeId: selected.employeeId,
            pool: selected.pool,
            units: Number(units),
            note,
          },
        },
      });
      const result = res.data?.adjustLeaveBalance;
      if (result?.status !== "SUCCESS") throw new Error(result?.message || "Adjustment failed");
      setUnits("");
      setNote("");
      refetch();
      refetchLedger();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRunAccrual = async () => {
    const res = await runAccrual();
    alert(res.data?.runLeaveAccrual?.message || "Done");
    refetch();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3 gap-4">
        <input
          placeholder="Search professional"
          className="border p-2 rounded w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            Days available this period. Click a balance for its history and to adjust it.
          </span>
          <button
            onClick={handleRunAccrual}
            className="text-sm px-3 py-2 border rounded bg-white hover:bg-gray-100"
            title="The daily job does this at 00:10; use after adding a professional"
          >
            Credit current period now
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : result?.status === "ERROR" ? (
        <p className="text-sm text-red-600">Couldn't load balances: {result.message}</p>
      ) : pools.length === 0 ? (
        <p className="text-sm text-gray-500">
          No paid leave type has its own balance yet. In Policies, make a type Paid with its own
          balance and how many days it credits.
        </p>
      ) : (result?.rows || []).length === 0 ? (
        <p className="text-sm text-gray-500">No employed professionals found.</p>
      ) : (
        <table className="min-w-full text-left text-sm text-gray-600">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="py-2 px-3">Professional</th>
              {pools.map((p) => (
                <th key={p} className="py-2 px-3">
                  {p}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.employeeId} className="border-b hover:bg-gray-50">
                <td className="py-2 px-3 font-medium text-gray-800">{row.fullName}</td>
                {row.balances.map((b) => (
                  <td key={b.pool} className="py-2 px-3">
                    <button
                      onClick={() => openLedger(row, b.pool)}
                      className={`underline decoration-dotted ${
                        b.balance <= 0 ? "text-red-600" : "text-gray-800"
                      }`}
                    >
                      {fmt(b.balance)}
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                {selected.fullName} · {selected.pool}
              </h2>
              <button
                onClick={() => setSelected(null)}
                className="text-gray-500 hover:text-gray-800 text-xl leading-none"
              >
                ×
              </button>
            </div>

            <form onSubmit={submitAdjustment} className="flex items-end gap-2 mb-4 p-3 bg-gray-50 rounded border">
              <label className="block">
                <span className="block text-xs font-medium mb-1">Days (+ credit / − debit)</span>
                <input
                  required
                  type="number"
                  step="0.5"
                  className="w-28 border p-2 rounded"
                  value={units}
                  onChange={(e) => setUnits(e.target.value)}
                />
              </label>
              <label className="block flex-1">
                <span className="block text-xs font-medium mb-1">Reason</span>
                <input
                  required
                  className="w-full border p-2 rounded"
                  placeholder="e.g. Opening balance, worked on 2 Oct holiday"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <button type="submit" className="px-4 py-2 rounded bg-blue-600 text-white">
                Adjust
              </button>
            </form>
            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

            {ledgerLoading ? (
              <p className="text-sm text-gray-500">Loading...</p>
            ) : ledger.length === 0 ? (
              <p className="text-sm text-gray-500">No entries yet.</p>
            ) : (
              <table className="min-w-full text-left text-sm text-gray-600">
                <thead className="bg-gray-100 text-gray-700">
                  <tr>
                    <th className="py-2 px-3">Date</th>
                    <th className="py-2 px-3">Entry</th>
                    <th className="py-2 px-3 text-right">Days</th>
                    <th className="py-2 px-3">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((l) => (
                    <tr key={l.id} className="border-b">
                      <td className="py-1.5 px-3 whitespace-nowrap">{l.date}</td>
                      <td className="py-1.5 px-3">{l.entryType}</td>
                      <td
                        className={`py-1.5 px-3 text-right ${
                          l.units < 0 ? "text-red-600" : "text-green-700"
                        }`}
                      >
                        {l.units > 0 ? "+" : ""}
                        {fmt(l.units)}
                      </td>
                      <td className="py-1.5 px-3">{l.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveBalances;
